import _extends from "@babel/runtime/helpers/extends";

function _createForOfIteratorHelperLoose(o, allowArrayLike) { var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"]; if (it) return (it = it.call(o)).next.bind(it); if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; return function () { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

import moment from "moment";
import { DATE_FORMAT_PRODUCT } from "../constants";
import { termPremiumInputs } from "./data/previewValues";
/**
 * Turn an XML string in a DOM document (#document) that can be queried
 * @param {string} xmlString - The XML as a string
 * @return {#document} - A DOM document
 */

export function turnXmlStringIntoDomDocument(xmlString) {
  var parser = new window.DOMParser(); // Set window so test utilities work

  return parser.parseFromString(xmlString, "application/xml");
}
/**
 * In the policy XML, find the latest <Term> (in <Terms>)
 * @param {DOMElement} xmlNode - the node we search in
 * @return {DOMElement} The node element of the latest <Term>
 */

export function getLatestTermNode(xmlNode) {
  return xmlNode.querySelector("Terms Term:last-child");
}
/**
 * In the policy XML, find the <Customers> node
 * @param {DOMElement} xmlNode - the node we search in
 * @return {DOMElement} The node element of for <Customers>
 */

export function getCustomersNode(xmlNode) {
  return xmlNode.querySelector("Customers");
}
/**
 * In the policy XML, find the latest <Interval> (in <Intervals>) in the latest
 * <Term> (in <Terms)
 * @param {DOMElement} xmlNode - the node we search in
 * @return {DOMElement} The node element of the latest <Interval>
 */

export function getLatestIntervalNode(xmlNode) {
  var latestTerm = getLatestTermNode(xmlNode);
  return latestTerm ? latestTerm.querySelector("Intervals Interval:last-child") : null;
}
/**
 * Creating a mapping of <DataItem> names to values. For example:
 *
 *   In:
 *   ["CoverageA", "CoverageB"]
 *   <Terms>
 *     <Term>
 *       <DataItem name="OpCoverageA" value="100000" />
 *       <DataItem name="OpCoverageB" value="100" />
 *     </Term>
 *   </Terms>
 *
 *   Out:
 *   {
 *     CoverageA: "100000"
 *     CoverageB: "100"
 *   }
 *
 * @param  {string[]} names - List of <DataItem> name attributes to find
 * @param  {DOMElement} xmlNode - The XML node to search in
 * @param  {boolean} prefixWithOp - Find names that start with `Op`
 * @return {Object} - Mapping of term names to values
 */

export function mapDataItemNamesToValues(names, xmlNode, prefixWithOp) {
  if (prefixWithOp === void 0) {
    prefixWithOp = true;
  }

  function getDataItemValue(name) {
    var fullName = prefixWithOp ? "Op" + name : name;
    var node = xmlNode.querySelector("DataItem[name=\"" + fullName + "\"]");
    return node ? node.getAttribute("value") : null;
  }

  return names.reduce(function (acc, name) {
    var _ref;

    var value = getDataItemValue(name);
    return _extends({}, acc, value ? (_ref = {}, _ref[name] = value, _ref) : null);
  }, {});
}
/**
 * Take the preview XML response, pull the values we need, and return an object
 * mapping those names to their values.
 * @param {string} xmlString – The XML returned from the preview XML response
 * @param {string} policyActionId
 * @return {{ policyData: Object, rulesets: Array}} – [policyData]: Mapping of term names to values from the preview XML
 *   depending on which policy action was run. [rulesets]: Array of server-side ruleset responses.
 */

export function getValuesFromTransactionRequestXml(xmlString, policyActionId) {
  var out = {
    policyData: {},
    rulesets: []
  };
  var xmlNode = turnXmlStringIntoDomDocument(xmlString); // If the XML isn't an insurance policy, just quit now.

  if (!xmlNode.querySelector("InsurancePolicy")) {
    return out;
  } // Collect rulesets


  var rulesetNodeList = xmlNode.querySelectorAll("InsurancePolicy > Quoting > CurrentQuote > RulesetResults > RulesetResult");

  for (var _iterator = _createForOfIteratorHelperLoose(rulesetNodeList.values()), _step; !(_step = _iterator()).done;) {
    var _rulesetNode$querySel, _rulesetNode$querySel2;

    var rulesetNode = _step.value;
    var ruleset = {};
    ruleset.result = rulesetNode.getAttribute("result");
    ruleset.id = rulesetNode.getAttribute("rulesetId");
    ruleset.trueResult = rulesetNode.getAttribute("trueResult");
    ruleset.name = ((_rulesetNode$querySel = rulesetNode.querySelector("[name='rulesetName']")) == null ? void 0 : _rulesetNode$querySel.getAttribute("value")) || "";
    ruleset.message = ((_rulesetNode$querySel2 = rulesetNode.querySelector("[name='rulesetMessage']")) == null ? void 0 : _rulesetNode$querySel2.getAttribute("value")) || "";
    out.rulesets.push(ruleset);
  } // If it's an `insuredcancel` form, we're gonna grab different data than if
  // it's any other form.


  if (policyActionId === "insuredcancel") {
    var eventNode = xmlNode.querySelector("EventHistory Event[type='Cancel']:last-child");

    if (eventNode) {
      // Map each <DataItem name="" value="" /> in the `<Event> to an object of
      // name/value pairs where we can destructure from. Example:
      //   In: <DataItem name="reasonCode" value="12" />
      //   Out: { reasonCode: "12" }
      var _Array$from$reduce = Array.from(eventNode.querySelectorAll("DataItem")).reduce(function (acc, DataItem) {
        var _extends2;

        return _extends({}, acc, (_extends2 = {}, _extends2[DataItem.getAttribute("name")] = DataItem.getAttribute("value"), _extends2));
      }, {}),
          reasonCode = _Array$from$reduce.reasonCode,
          reasonCodeLabel = _Array$from$reduce.reasonCodeLabel,
          CancelAmount = _Array$from$reduce.CancelAmount,
          ChangeInPremium = _Array$from$reduce.ChangeInPremium,
          ChangeInFee = _Array$from$reduce.ChangeInFee,
          ChangeInOthers = _Array$from$reduce.ChangeInOthers,
          ChangeInTax = _Array$from$reduce.ChangeInTax; // Assign the values from the event to values in our
      // `insuredCancelPreviewFields` object, found in `src/shared/data/previewValues.js`


      out.policyData = {
        cancellationType: reasonCode === "15" ? "Short-Rate" : "Pro Rata",
        cancellationReason: reasonCode + " - " + reasonCodeLabel,
        changeInPremiums: ChangeInPremium,
        changeInFees: ChangeInFee,
        changeInTaxes: ChangeInTax,
        changeInOthers: ChangeInOthers,
        totalChange: CancelAmount
      };
    }
  } else {
    // Get the values we need from `termPremiumInputs`, as that's what drives
    // the things that get displayed on the preview screen
    var opPrefixedTermNames = termPremiumInputs.map(function (input) {
      return input.name;
    });
    var termNames = termPremiumInputs.map(function (input) {
      return input.change;
    });
    out.policyData = _extends({}, mapDataItemNamesToValues(opPrefixedTermNames, getLatestTermNode(xmlNode)), mapDataItemNamesToValues(termNames, getLatestTermNode(xmlNode), false));
  }

  return out;
}
/**
 * Encode values to XML
 * https://stackoverflow.com/questions/7918868/how-to-escape-xml-entities-in-javascript
 *
 * @param {String} xmlStr
 * @return {String}
 */

export function encodeXml(xmlStr) {
  // Garbage in, garbage out
  if (typeof xmlStr !== "string") {
    return xmlStr;
  }

  return xmlStr.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&apos;");
}
/**
 * Decode values from XML
 *
 * @param {String} xmlStr
 * @return {String}
 */

export function decodeXml(xmlStr) {
  // Garbage in, garbage out
  if (typeof xmlStr !== "string") {
    return xmlStr;
  }

  return xmlStr.replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&apos;/g, "'");
}
/**
 * Extract the data we will need from the `policyXmlDom` prop. FWIW: this data
 * gets passed into <Rex /> as the initial values, many of the values won't be
 * relevant to Rex but you should make sure there are no name collisions with
 * values from the policy xml.
 *
 * @param {XMLDocument} policyXmlDom
 * @return {Object}
 */

export function extractDataFromPolicyXml(policyXmlDom) {
  // Not what we're expecting? Just return an empty object.
  if (!(policyXmlDom && policyXmlDom.documentElement && policyXmlDom.documentElement.tagName === "InsurancePolicy")) {
    return {};
  } // <Customers> node


  var customersNode = getCustomersNode(policyXmlDom); // In the latest <Term> node, we don't actually need to look through the child
  // <Intervals>. So we clone the latest <Term> then remove <Intervals> node
  // This makes querying WAY faster (like up to 5 seconds faster for large
  // policies) because we don't have to look through all child <Interval>
  // nodes, which have like 2000 items a piece in them.

  var latestTermNode = getLatestTermNode(policyXmlDom).cloneNode(true);
  var latestTermIntervalsNode = latestTermNode.getElementsByTagName("Intervals");

  if (latestTermIntervalsNode && latestTermIntervalsNode.length > 0) {
    latestTermNode.removeChild(latestTermIntervalsNode[0]);
  } // Find all <DataItem name="Op*"> nodes from the <Customers> node and
  // the latest <Term> node, then map the `name` attribute of each (minus
  // the "Op" prefix) to the `value` attribute.
  // This serves as a giant dictionary where we can lookup values of the policy
  // for both Rex and the Supplemental Terms.


  var latestTermAndCustomersData = [latestTermNode, customersNode].reduce(function (acc, nodeContext) {
    var valuesByName = [].slice.call(nodeContext.querySelectorAll("DataItem[name^='Op']")).reduce(function (nestedAcc, DataItem) {
      var _extends3;

      var name = DataItem.getAttribute("name").substring(2);
      var value = DataItem.getAttribute("value");
      return _extends({}, nestedAcc, (_extends3 = {}, _extends3[name] = value, _extends3));
    }, {});
    return _extends({}, acc, valuesByName);
  }, {}); // Get all the flags from the `<Flags>` node and map their names to values
  // Note: we prefix the `name` with `Flag` to avoid namespace issues with other
  // values in the policy

  var flagsData = [].slice.call(policyXmlDom.querySelectorAll("Flags Flag")).reduce(function (acc, Flag) {
    var _extends4;

    var name = Flag.getAttribute("name");
    var value = Flag.getAttribute("value");
    return _extends({}, acc, (_extends4 = {}, _extends4["Flag" + name] = value, _extends4));
  }, {});
  return _extends({}, latestTermAndCustomersData, flagsData, {
    // Other data from the XML needed at various places in Pac
    // Mimics the model of the policy XML
    InsightPolicyId: policyXmlDom.querySelector('Identifier[name="InsightPolicyId"]').getAttribute("value"),
    PolicyID: policyXmlDom.querySelector('Identifier[name="PolicyID"]').getAttribute("value"),
    Version: policyXmlDom.querySelector("Management Version").textContent,
    PolicyState: policyXmlDom.querySelector("Management PolicyState").textContent,
    // Effective Date min/max values.
    //
    // Some background:
    //
    // There seems to be a reasonable expectation that if you pull dates from
    // `<StartDate>...</StartDate>` and `<ExpirationDate>...</ExpirationDate>`,
    // they will be a timestamp in ISO8601 format (i.e. 2014-08-15T00:00:00-04:00)
    // because those values are handled by software on the backend.
    //
    // On the other hand, product values in the policy (i.e.  `<DataItem name="OpExpirationDate" value="...">`)
    // which constitute dates will come in a `MM/DD/YYYY` format. So we try to
    // normalize all dates we pull from the policy XML to come in the `MM/DD/YYYY`
    // format for consistency.
    //
    // Minimum effective date:
    //   Save as: `StartDate`
    //   Save format: MM/DD/YYYY (to match all other "product values" in the XML)
    //   Location: `InsurancePolicy Terms Term:last-child Intervals Interval:last-child StartDate`
    //   Location format: YYYY-MM-DD
    // We have to pull the minimum effective date from `<StartDate>` because
    // there is no other representation of this date in the policy. From ChrisW
    // of the product implementation team: "the date in /InsurancePolicy/Terms[last()]/Intervals[last()]/<StartDate>
    // is not parsed into any actual 'Op' variables".
    //
    // Maximum effective date:
    //   Save as: `ExpirationDate`
    //   Save format: MM/DD/YYYY
    //   Location: `InsurancePolicy Terms Term:last-child <DataItem name="OpExpirationDate">`
    //   Location format: MM/DD/YYYY
    // FWIW: This value could be manually pulled from `InsurancePolicy Terms Term:last-child ExpirationDate`
    // However, we're already getting it when we grab all "Op" values in the
    // latest term (see `latestTermAndCustomersData`). So we'll just use that
    // then we don't have to convert date formats.
    // From ChrisW of the product implementation team: ""`OpExpirationDate` will
    // change each term however and is the same as `/InsurancePolicy/Terms[last()]/<ExpirationDate>`"
    //
    // - Jim Nielsen, Jun 28, 2018
    StartDate: moment(stripTimeFromDate(getLatestIntervalNode(policyXmlDom).querySelector("StartDate").textContent), "YYYY-MM-DD").format(DATE_FORMAT_PRODUCT)
  });
}
/**
 * Strip time from ISO8601 timestamp
 * 2016-12-22T23:00:00-04:00 -> 2016-12-22
 *
 * @param {string} dateStr
 * @return {string}
 */

function stripTimeFromDate(dateStr) {
  var tPosition = dateStr.indexOf("T");
  return tPosition !== -1 ? dateStr.substring(0, tPosition) : dateStr;
}